import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, CardContent, CardHeader, CardTitle,
  Button, Badge,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/UI';
import MainLayout from '../Layout/MainLayout';
import apiClient from '../../utils/api';
import {
  Plus, DollarSign, TrendingUp, Calendar, CheckCircle,
  BarChart3, RefreshCw, FileText, AlertCircle
} from 'lucide-react';

const BUDGET_STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-500', icon: FileText },
  active: { label: 'Active', color: 'bg-green-500', icon: CheckCircle },
  completed: { label: 'Completed', color: 'bg-blue-500', icon: CheckCircle },
  archived: { label: 'Archived', color: 'bg-gray-400', icon: FileText }
};

const BudgetAgentDashboard = () => {
  const navigate = useNavigate();
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fiscalYearFilter, setFiscalYearFilter] = useState('all');
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    draft: 0,
    totalAmount: 0
  });

  useEffect(() => {
    fetchBudgets();
  }, [fiscalYearFilter]);

  const fetchBudgets = async () => {
    try {
      setLoading(true);
      const params = fiscalYearFilter !== 'all' ? { fiscal_year: fiscalYearFilter } : {};
      const response = await apiClient.get('/api/budget-agent/budgets', { params });

      if (response && response.budgets) {
        setBudgets(response.budgets);
        calculateStats(response.budgets);
      } else {
        setBudgets([]);
        calculateStats([]);
      }
    } catch (error) {
      console.error('Error fetching budgets:', error);
      setBudgets([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (budgetList) => {
    const totalAmount = budgetList.reduce((sum, b) => sum + (b.total_amount || 0), 0);
    setStats({
      total: budgetList.length,
      active: budgetList.filter(b => b.status === 'active').length,
      draft: budgetList.filter(b => b.status === 'draft').length,
      totalAmount
    });
  };

  const startNewBudget = () => {
    navigate('/budget-agent/new');
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const StatCard = ({ title, value, icon: Icon, color, subtext }) => (
    <Card className="relative overflow-hidden">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{value}</p>
            {subtext && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{subtext}</p>
            )}
          </div>
          <div className={`p-3 rounded-xl ${color}`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <MainLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <BarChart3 className="w-7 h-7 text-indigo-500" />
              Budget Agent
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              AI-powered budget creation and management
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={fetchBudgets}
              className="flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
            <Button
              onClick={startNewBudget}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700"
            >
              <Plus className="w-4 h-4" />
              New Budget
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="Total Budgets"
            value={stats.total}
            icon={FileText}
            color="bg-indigo-500"
          />
          <StatCard
            title="Active Budgets"
            value={stats.active}
            icon={CheckCircle}
            color="bg-green-500"
          />
          <StatCard
            title="Draft Budgets"
            value={stats.draft}
            icon={AlertCircle}
            color="bg-yellow-500"
          />
          <StatCard
            title="Total Budget Amount"
            value={formatCurrency(stats.totalAmount)}
            icon={DollarSign}
            color="bg-blue-500"
          />
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Fiscal Year:
                </span>
              </div>
              <select
                value={fiscalYearFilter}
                onChange={(e) => setFiscalYearFilter(e.target.value)}
                className="w-32 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="all">All Years</option>
                <option value="2026">2026</option>
                <option value="2025">2025</option>
                <option value="2024">2024</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Budgets Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Saved Budgets
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
                <span className="ml-2 text-gray-500">Loading budgets...</span>
              </div>
            ) : budgets.length === 0 ? (
              <div className="text-center py-12">
                <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No budgets yet
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  Get started by creating your first AI-powered budget
                </p>
                <Button
                  onClick={startNewBudget}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Budget
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Budget Name</TableHead>
                    <TableHead>Fiscal Year</TableHead>
                    <TableHead>Subsidiary</TableHead>
                    <TableHead>Categories</TableHead>
                    <TableHead>Total Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {budgets.map((budget) => {
                    const statusConfig = BUDGET_STATUS_CONFIG[budget.status] || BUDGET_STATUS_CONFIG.draft;
                    return (
                      <TableRow key={budget.id}>
                        <TableCell className="font-medium">{budget.name}</TableCell>
                        <TableCell>{budget.fiscal_year}</TableCell>
                        <TableCell>{budget.subsidiary_name || '-'}</TableCell>
                        <TableCell>{budget.category_count || 0}</TableCell>
                        <TableCell>{formatCurrency(budget.total_amount)}</TableCell>
                        <TableCell>
                          <Badge className={`${statusConfig.color} text-white`}>
                            {statusConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(budget.created_at)}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/budget-agent/${budget.id}`)}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default BudgetAgentDashboard;
